/**
 * Favourites kept in this browser (localStorage, by product handle):
 * - <favorite-toggle data-handle> wraps the heart button on cards and the product page
 * - .favorites-count badges show the number of saved products; the count lives on <html>
 *   (--favorites-count, [data-has-favorites]) so header re-renders cannot wipe it
 * - <favorites-list> renders the saved products as listing cards on the favourites page
 */

const STORAGE_KEY = 'despina-favorites';
const MAX_ITEMS = 60;
const CHANGE_EVENT = 'despina:favorites-change';

/** @returns {string[]} */
function readFavorites() {
  try {
    const list = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(list) ? list.filter((handle) => typeof handle === 'string') : [];
  } catch {
    return [];
  }
}

/** @param {string[]} list */
function writeFavorites(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_ITEMS)));
  } catch {
    // Storage blocked (private mode); the heart still reflects the click for this page view
  }
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { list } }));
}

/** Runs `callback` on every favourites change, from this tab or another one */
function onFavoritesChange(callback, signal) {
  document.addEventListener(CHANGE_EVENT, callback, { signal });
  window.addEventListener(
    'storage',
    (event) => {
      if (event.key === STORAGE_KEY) callback();
    },
    { signal }
  );
}

class FavoriteToggle extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.querySelector('button')?.addEventListener('click', this.#toggle, { signal });
    onFavoritesChange(this.#sync, signal);
    this.#sync();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /** @param {MouseEvent} event */
  #toggle = (event) => {
    // The heart sits inside cards whose image is a link
    event.preventDefault();
    event.stopPropagation();

    const handle = this.dataset.handle;
    if (!handle) return;

    const list = readFavorites();
    const index = list.indexOf(handle);
    if (index === -1) {
      list.unshift(handle);
      this.#publishAdded();
    } else {
      list.splice(index, 1);
    }
    writeFavorites(list);
  };

  /** Tells the store's pixels (Settings → Customer events) that a product was saved */
  #publishAdded() {
    const { variantId, title, price, currency } = this.dataset;
    window.Shopify?.analytics?.publish?.('despina:favorite_added', {
      variant_id: variantId,
      title,
      price: Number(price) / 100,
      currency,
    });
  }

  #sync = () => {
    const button = this.querySelector('button');
    if (!button) return;

    const saved = readFavorites().includes(this.dataset.handle ?? '');
    button.setAttribute('aria-pressed', String(saved));
    const label = saved ? this.dataset.labelRemove : this.dataset.labelAdd;
    if (label) button.setAttribute('aria-label', label);
  };
}

/** Publishes the count on <html> for the CSS-drawn .favorites-count badges */
function syncCount() {
  const count = readFavorites().length;
  const root = document.documentElement;
  root.style.setProperty('--favorites-count', String(count));
  root.toggleAttribute('data-has-favorites', count > 0);
}

class FavoritesList extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    this.#controller = new AbortController();
    onFavoritesChange(this.#removeUnsaved, this.#controller.signal);
    this.#render();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  get #grid() {
    return this.querySelector('[data-favorites-grid]');
  }

  get #empty() {
    return this.querySelector('[data-favorites-empty]');
  }

  async #render() {
    const grid = this.#grid;
    if (!grid) return;

    const handles = readFavorites();
    if (handles.length === 0) {
      this.#showEmpty();
      return;
    }

    this.setAttribute('aria-busy', 'true');
    const root = window.Shopify?.routes?.root ?? '/';
    const signal = this.#controller?.signal;

    // Each card comes from the product's "favori-kart" view, which renders the listing card snippet
    const cards = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`${root}products/${encodeURIComponent(handle)}?view=favori-kart`, { signal });
          if (response.ok) return { handle, html: await response.text(), gone: false };
          return { handle, html: null, gone: response.status === 404 };
        } catch {
          return { handle, html: null, gone: false };
        }
      })
    );
    if (signal?.aborted) return;

    // Forget products that no longer exist (404), keep the ones that only failed to load
    const missing = cards.filter((card) => card.gone).map((card) => card.handle);
    if (missing.length) writeFavorites(readFavorites().filter((handle) => !missing.includes(handle)));

    const items = [];
    for (const card of cards) {
      if (!card.html) continue;
      const item = document.createElement('li');
      item.className = 'product-grid__item';
      item.dataset.handle = card.handle;
      item.innerHTML = card.html;
      items.push(item);
    }

    grid.replaceChildren(...items);
    this.removeAttribute('aria-busy');
    if (items.length === 0) this.#showEmpty();
  }

  /** Drops cards whose heart was switched off on this page */
  #removeUnsaved = () => {
    const saved = readFavorites();
    for (const item of this.querySelectorAll('[data-favorites-grid] > [data-handle]')) {
      if (item instanceof HTMLElement && !saved.includes(item.dataset.handle ?? '')) item.remove();
    }
    if (!this.querySelector('[data-favorites-grid] > [data-handle]')) this.#showEmpty();
  };

  #showEmpty() {
    this.#grid?.replaceChildren();
    const empty = this.#empty;
    if (empty instanceof HTMLElement) empty.hidden = false;
    this.removeAttribute('aria-busy');
  }
}

if (!customElements.get('favorite-toggle')) customElements.define('favorite-toggle', FavoriteToggle);
if (!customElements.get('favorites-list')) customElements.define('favorites-list', FavoritesList);

syncCount();
onFavoritesChange(syncCount);
